import React from 'react';
import {
  Users, ClipboardList, Truck, CreditCard, FileCheck, FileText,
  CheckCircle2, AlertTriangle, XCircle, ChevronRight,
} from 'lucide-react';

/* ─── Design Tokens (match BookingPanels) ─── */
const PANEL_BG = '#171411';
const SURFACE = '#1e1b16';
const BORDER = '#2a2620';
const MONO = "'DM Mono', monospace";
/* Panel headings render at 18px — under the 1.375rem display floor — so they take
   the mono body face at 500 rather than the display serif. */
const DISPLAY = 'var(--s8-font-mono)';
const BODY = "'Barlow', sans-serif";

const GREEN = '#3CCB7F';
const AMBER = '#E6A23C';
const RED = '#EF4444';
const MUTED = '#666';

/* ─── Dynamic Helpers (non-demo bookings) ─── */

function buildCrewCheck(crewSlots) {
  const total = crewSlots.length;
  if (total === 0) return { status: 'warn', title: 'No crew defined', sub: 'Add required roles', critical: false };
  const filled = crewSlots.filter(s => s.assigned_to).length;
  const missing = crewSlots.filter(s => !s.assigned_to).map(s => s.role);
  if (filled === total) return { status: 'ok', title: 'Crew complete', sub: `All ${total} roles assigned`, critical: false };
  return {
    status: 'warn',
    title: 'Crew incomplete',
    sub: missing.map(r => `${r} missing`).join(', '),
    critical: true,
  };
}

function buildRosCheck(runOfShow) {
  if (runOfShow.length === 0) return { status: 'warn', title: 'Run of show missing', sub: 'No schedule defined', critical: false };
  return { status: 'ok', title: 'Run of show complete', sub: `${runOfShow.length} items scheduled`, critical: false };
}

function buildLogisticsCheck(logistics) {
  const fields = ['catering', 'travel', 'gear', 'downtime'];
  const filled = fields.filter(f => logistics?.[f]);
  if (filled.length === fields.length) return { status: 'ok', title: 'Logistics complete', sub: 'All details specified', critical: false };
  const missing = fields.filter(f => !logistics?.[f]);
  const labels = { catering: 'Catering', travel: 'Travel / parking', gear: 'Gear expectations', downtime: 'Downtime policy' };
  return {
    status: 'warn',
    title: 'Logistics incomplete',
    sub: missing.map(f => `${labels[f]} not specified`).slice(0, 2).join(' · '),
    critical: false,
  };
}

function buildPaymentCheck(snapshot) {
  const timing = snapshot?.paymentTiming;
  if (timing) {
    const labels = {
      same_night: 'Same night', within_24h: 'Within 24 hours',
      within_7_days: 'Within 7 days', within_14_days: 'Within 14 days', custom: 'Custom',
    };
    return { status: 'ok', title: 'Payment terms clear', sub: labels[timing] || timing, critical: false };
  }
  return { status: 'warn', title: 'Payment unclear', sub: 'No payment timing set', critical: false };
}

/* ─── Row Component ─── */

function ReadinessRow({ check, ctaLabel, onCta, testId, isLast }) {
  const color = check.status === 'ok' ? GREEN : AMBER;
  const StatusIcon = check.status === 'ok' ? CheckCircle2 : AlertTriangle;

  return (
    <div
      data-testid={testId}
      className="flex items-center gap-3 py-4 px-4"
      style={{ borderBottom: isLast ? 'none' : `1px solid ${BORDER}` }}
    >
      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        <StatusIcon size={16} color={color} className="shrink-0" />
        <div className="min-w-0">
          <p className="mb-1.5" style={{ fontFamily: BODY, fontSize: 14, fontWeight: 600, color: '#F0EBE0' }}>
            {check.title}
          </p>
          <p style={{ fontFamily: MONO, fontSize: 11, color: MUTED, lineHeight: 1.4 }} className="truncate hidden sm:block">
            {check.detail || check.sub}
          </p>
        </div>
      </div>

      {check.status !== 'ok' && ctaLabel && (
        <button
          onClick={onCta}
          data-testid={`${testId}-cta`}
          className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition-all hover:brightness-110"
          style={{
            fontFamily: MONO,
            fontSize: 11,
            letterSpacing: 0.3,
            color: AMBER,
            background: `${AMBER}12`,
            border: `1px solid ${AMBER}30`,
          }}
        >
          {ctaLabel}
          <ChevronRight size={12} />
        </button>
      )}
    </div>
  );
}

/* ─── Demo Checks (hardcoded for SHOWTIME) ─── */

function getDemoChecks(techRiderReviewed) {
  return [
    { key: 'crew',       status: 'warn', title: 'Crew incomplete',           sub: 'Stage Host missing',                                     cta: 'Assign crew',  panel: 'crew-slots-panel',    critical: true },
    techRiderReviewed
      ? { key: 'rider', status: 'ok',   title: 'Tech rider reviewed',       sub: 'Reviewed · Updated 8 Apr',                               cta: null,           panel: null,                  critical: false }
      : { key: 'rider', status: 'warn', title: 'Tech rider not reviewed',   sub: 'Last updated 8 Apr',                                     cta: 'Open rider',   panel: null,                  critical: true },
    { key: 'logistics',  status: 'warn', title: 'Logistics incomplete',      sub: 'Load-in access not specified · Parking not specified',   cta: 'Add details',  panel: null,                  critical: true },
    { key: 'contract',   status: 'ok',   title: 'Contract signed',           sub: 'Both parties · 12 Apr',                                  cta: null,           panel: null,                  critical: false },
    { key: 'payment',    status: 'ok',   title: 'Payment terms clear',       sub: 'Same night · IBAN ending 4421',                          cta: null,           panel: null,                  critical: false },
    { key: 'ros',        status: 'ok',   title: 'Run of show complete',      sub: '9 items scheduled',                                      cta: null,           panel: 'run-of-show-panel',   critical: false },
  ];
}

/* ─── Main Component ─── */

export function ShowReadiness({ crewSlots, runOfShow, guestList, snapshot, logistics, onScrollTo, isDemo, techRiderReviewed }) {
  let checks;

  if (isDemo) {
    checks = getDemoChecks(techRiderReviewed);
  } else {
    checks = [
      { key: 'crew',       ...buildCrewCheck(crewSlots),       cta: 'Assign crew',  panel: 'crew-slots-panel' },
      { key: 'ros',        ...buildRosCheck(runOfShow),        cta: 'Edit',         panel: 'run-of-show-panel' },
      { key: 'logistics',  ...buildLogisticsCheck(logistics),  cta: 'Add details',  panel: null },
      { key: 'payment',    ...buildPaymentCheck(snapshot),     cta: 'Edit',         panel: null },
    ];
  }

  const warnCount = checks.filter(c => c.status === 'warn').length;
  const hasCritical = checks.some(c => c.status === 'warn' && c.critical);
  const allOk = warnCount === 0;

  let globalColor, globalLabel, GlobalIcon;
  if (allOk) {
    globalColor = GREEN; globalLabel = 'Ready'; GlobalIcon = CheckCircle2;
  } else if (hasCritical) {
    globalColor = RED; globalLabel = `${warnCount} blocker${warnCount !== 1 ? 's' : ''}`; GlobalIcon = XCircle;
  } else {
    globalColor = AMBER; globalLabel = 'At Risk'; GlobalIcon = AlertTriangle;
  }

  const handleCta = (panelId) => {
    if (!panelId) return;
    if (onScrollTo) { onScrollTo(panelId); return; }
    const el = document.querySelector(`[data-testid="${panelId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      const toggle = el.querySelector(`[data-testid="${panelId}-toggle"]`);
      if (toggle) setTimeout(() => toggle.click(), 400);
    }
  };

  return (
    <div
      data-testid="show-readiness"
      style={{
        background: PANEL_BG,
        borderRadius: 16,
        borderTop: '1px solid rgba(212, 169, 90, 0.12)',
        borderRight: '1px solid rgba(212, 169, 90, 0.12)',
        borderBottom: '1px solid rgba(212, 169, 90, 0.12)',
        borderLeft: hasCritical ? `2px solid ${RED}` : '1px solid rgba(212, 169, 90, 0.12)',
        boxShadow: '0 0 20px rgba(0,0,0,0.3)',
      }}
      className="mb-6 overflow-hidden"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4"
        style={{ borderBottom: `1px solid ${BORDER}` }}
      >
        <div>
          <h2
            style={{ fontFamily: DISPLAY, fontWeight: 500, fontSize: 18, letterSpacing: '0.04em', color: '#F0EBE0' }}
            data-testid="show-readiness-title"
          >
            SHOW READINESS
          </h2>
          <p style={{ fontFamily: MONO, fontSize: 11, color: '#a39889', marginTop: 2 }}>
            What still needs attention before showtime
          </p>
        </div>

        <div
          data-testid="show-readiness-status"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full shrink-0 ${globalColor === RED ? 's8-heartbeat' : ''}`}
          style={{
            background: `${globalColor}14`,
            border: `1px solid ${globalColor}40`,
            whiteSpace: 'nowrap',
            minWidth: 'fit-content',
            ...(globalColor === RED ? { '--hb-color': `${globalColor}55` } : {}),
          }}
        >
          <GlobalIcon size={14} color={globalColor} />
          <span style={{ fontFamily: MONO, fontSize: hasCritical ? 13 : 11, fontWeight: hasCritical ? 700 : 600, color: globalColor, letterSpacing: 0.5 }}>
            {globalLabel}
          </span>
        </div>
      </div>

      {/* Rows */}
      <div style={{ background: SURFACE }}>
        {checks.map((check, i) => (
          <ReadinessRow
            key={check.key}
            check={check}
            ctaLabel={check.cta}
            onCta={() => handleCta(check.panel)}
            testId={`readiness-check-${check.key}`}
            isLast={i === checks.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
